import { Injectable, inject } from '@angular/core';
import {
  Firestore, arrayRemove, arrayUnion, deleteField, doc, getDoc, serverTimestamp, setDoc, updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';

import { EntraSegmentField, UsageReportSegment } from '../models/entra-segments';
import { Organization } from '../models/organization.model';
import { orgLogoPath } from '../utils/org-logo';
import { coachingEnabledFrom } from '../utils/coaching-setting';
import { DEFAULT_RESIDENCY_REGION, ResidencyRegion, isResidencyRegion } from '../models/residency';
import { DataStoreService } from './data-store.service';
import { FirebaseService } from './firebase.service';
import { UserService } from './user.service';
import { environment } from '../../../environments/environment';

interface OrganizationData {
  name: string;
  ownerId: string;
  createdAt: any;
  licenseAccepted: boolean;
  acceptedBy?: string;
  acceptedAt?: any;
  licenseToken?: string;
}

type CreateOrganizationOptions = {
  licenseAccepted?: boolean;
  // Referral attribution: managing partner org id (validated server-side).
  partnerId?: string;
  // Data residency: where the org's data lives (absent → Canada). Permanent.
  region?: ResidencyRegion;
};

export enum OrganizationErrorCodes {
  OrganizationNotFound = 'organization-not-found',
  OrganizationUnauthorized = 'organization-unauthorized',
  UserNotAuthenticated = 'user-not-authenticated',
  FailToCreateOrganization = 'fail-to-create-organization',
}

export class OrganizationError extends Error {
  constructor(readonly code: OrganizationErrorCodes) {
    super(code);
    this.name = 'OrganizationError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly firebase = inject(FirebaseService);
  private readonly dataStore = inject(DataStoreService);
  private readonly userService = inject(UserService);

  async getOrganization(
    organizationId: string,
    updateCurrent = false,
    knownOrganizationIds?: string[],
    // Data residency: Firestore of the org's regional project, when the caller
    // resolved it (loading every org of the user). Defaults to the active org's.
    db?: Firestore,
  ): Promise<Organization> {
    const current = this.dataStore.getProperty('currentOrganization');
    if (current && current.id === organizationId && !updateCurrent) {
      return current;
    }

    // Verify user is authenticated
    const currentUser = this.firebase.auth.currentUser;
    if (!currentUser) {
      throw new OrganizationError(OrganizationErrorCodes.UserNotAuthenticated);
    }

    // Verify user has access to this organization. Callers that already hold
    // the user's org list (e.g. OrgContext loading every org at once) pass it
    // in so we skip a redundant user-doc read per org.
    let organizationIds = knownOrganizationIds;
    if (!organizationIds) {
      const userDoc = await getDoc(doc(this.firebase.globalFirestore, 'users', currentUser.uid));
      organizationIds = (userDoc.data()?.['organizationIds'] as string[]) || [];
    }

    if (!organizationIds.includes(organizationId)) {
      throw new OrganizationError(OrganizationErrorCodes.OrganizationUnauthorized);
    }

    const orgDb = db ?? this.firebase.firestore;
    const orgDoc = await getDoc(doc(orgDb, 'organizations', organizationId));
    if (!orgDoc.exists()) {
      throw new OrganizationError(OrganizationErrorCodes.OrganizationNotFound);
    }

    const data = orgDoc.data();

    // Auto-migrate legacy single-tenant field to tids array. Derive `tids`
    // in-memory unconditionally so callers get the right shape; persisting the
    // migration is best-effort. Only org admins/owners can write the org doc,
    // so a plain member loading a still-unmigrated org would otherwise hit
    // permission-denied here — which, on the bootstrap path, blanks the whole
    // app. An admin load (or backend) will persist it later.
    if (data['tid'] && !data['tids']?.length) {
      data['tids'] = [data['tid']];
      try {
        const orgRef = doc(orgDb, 'organizations', organizationId);
        await updateDoc(orgRef, { tids: [data['tid']], tid: deleteField() });
      } catch {
        // Member without write access — non-fatal; tids is already set above.
      }
    }

    const organization: Organization = {
      id: organizationId,
      name: data['name'],
      logoUrl: typeof data['logoUrl'] === 'string' && data['logoUrl'] ? data['logoUrl'] : null,
      ownerId: data['ownerId'],
      createdAt: data['createdAt'],
      licenseAccepted: data['licenseAccepted'],
      licenseToken: data['licenseToken'],
      awsFulfillment: data['awsFulfillment'],
      stripeCustomerId: data['stripeCustomerId'],
      creditBalance: data['creditBalance'],
      overageAllowed: data['overageAllowed'] === true,
      tids: data['tids'] ?? [],
      usageReportSegments: data['usageReportSegments'],
      accounts: data['accounts'],
      milestones: data['milestones'],
      onboardingProgram: data['onboardingProgram'],
      // Partner flags drive white-label branding and catalog overrides — this
      // explicit field list must carry them or both silently no-op.
      isPartner: data['isPartner'],
      partnerId: data['partnerId'],
    };

    if (updateCurrent) {
      this.dataStore.updateState({ currentOrganization: organization });
    }

    return organization;
  }

  async createOrganization(
    organizationName: string,
    options: CreateOrganizationOptions = {},
  ): Promise<Organization> {
    const { licenseAccepted = false, partnerId, region } = options;
    const ownerId = this.firebase.auth.currentUser?.uid;
    if (!ownerId) {
      throw new OrganizationError(OrganizationErrorCodes.UserNotAuthenticated);
    }

    let result: { organizationId: string; creditsGranted: number };
    try {
      const callable = httpsCallable<
        { name: string; licenseAccepted: boolean; partnerId?: string; region?: ResidencyRegion },
        { organizationId: string; creditsGranted: number }
        // Limited-use App Check token — required for the backend's
        // consumeAppCheckToken (single-use / replay protection).
      >(this.firebase.globalFunctions, 'createOrganization', { limitedUseAppCheckTokens: true });
      // Org creation always goes to the CA (global) project's functions: it
      // owns orgDirectory + users and writes the org into its region.
      const resp = await callable({ name: organizationName, licenseAccepted, partnerId, ...(region ? { region } : {}) });
      result = resp.data;
    } catch (e) {
      console.error('Creating organization - Callable failed:', e);
      // Creating an organization needs a verified phone number (backend, 2026-10-24):
      // verify it on the auth portal, which brings the user back here.
      if ((e as { details?: { code?: string } } | null)?.details?.code === 'phone-verification-required') {
        window.location.assign(`${environment.authBaseUrl}/verify-phone?return_to=${encodeURIComponent(window.location.href)}`);
      }
      throw new OrganizationError(OrganizationErrorCodes.FailToCreateOrganization);
    }

    // Server-side seeding via the onboardOrganization finish-existing-org path:
    // product-access + integrations configs, model-access defaults, and the
    // product accounts. This replaces the legacy client-side seeding — the org
    // `accounts` map is on the Firestore rules' server-authoritative deny-list,
    // so clients can no longer link accounts themselves. Integrations are
    // passed explicitly: the backend default still seeds the legacy
    // 'google-search' id, which this console's catalog no longer has.
    try {
      const onboard = httpsCallable<
        { organizationId: string; integrations: string[] },
        { organizationId: string; creditsGranted: number }
        // Limited-use App Check token — the backend consumes tokens on this
        // callable; a fresh one per call keeps the shared cached token intact.
      >(this.firebase.globalFunctions, 'onboardOrganization', { limitedUseAppCheckTokens: true });
      await onboard({
        organizationId: result.organizationId,
        integrations: ['web-search', 'perplexity'],
      });
    } catch (e) {
      // The org exists (the portal re-runs onboarding for its owner later),
      // but surface the failure so the console doesn't switch into a
      // half-seeded org.
      console.error('Creating organization - Onboarding failed:', e);
      throw new OrganizationError(OrganizationErrorCodes.FailToCreateOrganization);
    }

    return {
      id: result.organizationId,
      name: organizationName,
      ownerId,
      createdAt: serverTimestamp(),
      licenseAccepted,
      creditBalance: result.creditsGranted,
      accounts: {},
    };
  }

  /**
   * Data region of an org from the global orgDirectory (read-only; set at
   * creation). A missing entry is a pre-directory org, i.e. Canada.
   */
  async getOrganizationRegion(organizationId: string): Promise<ResidencyRegion> {
    const snap = await getDoc(doc(this.firebase.globalFirestore, 'orgDirectory', organizationId));
    const region = snap.data()?.['region'];
    return isResidencyRegion(region) ? region : DEFAULT_RESIDENCY_REGION;
  }

  async renameOrganization(organizationId: string, newName: string): Promise<Organization> {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(organizationRef, { name: newName });

    const current = this.dataStore.getProperty('currentOrganization');
    if (current && current.id === organizationId) {
      const updated = { ...current, name: newName };
      this.dataStore.updateState({ currentOrganization: updated });
      return updated;
    }

    return this.getOrganization(organizationId, true);
  }

  /**
   * Upload a new org logo (validated by the caller — utils/org-logo) to the
   * org's regional bucket, point organizations/{id}.logoUrl at it and delete
   * the previous file. Storage + Firestore rules allow it for the org owner and
   * admin members only. The sdk trigger mirrors logoUrl into orgDirectory.
   */
  async setOrganizationLogo(organizationId: string, file: Blob & { type: string }): Promise<Organization> {
    const path = orgLogoPath(organizationId, file.type, Date.now());
    const fileRef = ref(this.firebase.storage, path);
    await uploadBytes(fileRef, file, { contentType: file.type, cacheControl: 'public, max-age=31536000' });
    const logoUrl = await getDownloadURL(fileRef);
    const previous = (await this.getOrganization(organizationId)).logoPath ?? null;
    await updateDoc(doc(this.firebase.firestore, 'organizations', organizationId), {
      logoUrl, logoPath: path, logoUpdatedAt: serverTimestamp(),
    });
    if (previous && previous !== path) {
      await deleteObject(ref(this.firebase.storage, previous)).catch(() => { /* already gone */ });
    }
    return this.patchCurrent(organizationId, { logoUrl, logoPath: path });
  }

  /** Remove the org logo: clear the fields first (the apps fall back to initials), then delete the file. */
  async removeOrganizationLogo(organizationId: string): Promise<Organization> {
    const previous = (await this.getOrganization(organizationId)).logoPath ?? null;
    await updateDoc(doc(this.firebase.firestore, 'organizations', organizationId), {
      logoUrl: deleteField(), logoPath: deleteField(), logoUpdatedAt: serverTimestamp(),
    });
    if (previous) {
      await deleteObject(ref(this.firebase.storage, previous)).catch(() => { /* already gone */ });
    }
    return this.patchCurrent(organizationId, { logoUrl: null, logoPath: null });
  }

  private async patchCurrent(organizationId: string, patch: Partial<Organization>): Promise<Organization> {
    const current = this.dataStore.getProperty('currentOrganization');
    if (current && current.id === organizationId) {
      const updated = { ...current, ...patch };
      this.dataStore.updateState({ currentOrganization: updated });
      return updated;
    }
    return this.getOrganization(organizationId, true);
  }

  async isOrganizationOwner(organizationId: string, userId: string): Promise<boolean> {
    const organization = await this.getOrganization(organizationId);
    return organization.ownerId === userId;
  }

  async addOrganizationToUser(userId: string, organizationId: string) {
    await this.userService.addOrganizationToUser(userId, organizationId);
  }

  async isLicenseAccepted(organizationId: string): Promise<boolean> {
    const organization = await this.getOrganization(organizationId);
    return !!organization.licenseAccepted;
  }

  async verifyLicenseToken(organizationId: string, token: string): Promise<boolean> {
    const callable = httpsCallable<
      { organizationId: string; token: string },
      { verified: boolean }
    >(this.firebase.functions, 'verifyLicenseTokenFromOrganization');
    const result = await callable({ organizationId, token });
    return result.data.verified;
  }

  async acceptLicense(organizationId: string, token: string): Promise<boolean> {
    const callable = httpsCallable<
      { organizationId: string; token: string },
      { success: boolean }
    >(this.firebase.functions, 'acceptLicenseForOrganization');
    const result = await callable({ organizationId, token });
    return result.data.success;
  }

  async acceptLicenseAsAdmin(organizationId: string, acceptedBy: string) {
    const organizationRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(organizationRef, {
      licenseAccepted: true,
      acceptedBy,
      acceptedAt: serverTimestamp(),
    });
  }

  async connectMicrosoftTenant(organizationId: string, tenantId: string): Promise<void> {
    const orgRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(orgRef, { tids: arrayUnion(tenantId) });
  }

  async disconnectMicrosoftTenant(organizationId: string, tenantId: string): Promise<void> {
    const orgRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(orgRef, { tids: arrayRemove(tenantId) });
  }

  /** @param tenantId Entra tid captured from the Microsoft sign-in response.
   *  When provided, the backend auto-discovers orgs linked to that tenant
   *  and joins the user to them.
   *  @param microsoftUserId The user's Entra OID claim (NOT Firebase's
   *  providerData.uid, which is the per-app `sub`). Lets the backend reach
   *  the right principal in Microsoft Graph. */
  async syncMicrosoftRoleOnLogin(tenantId?: string, microsoftUserId?: string): Promise<void> {
    const fn = httpsCallable<
      { tenantId?: string; microsoftUserId?: string },
      { synced: boolean }
    >(this.firebase.globalFunctions, 'syncMicrosoftRoleOnLogin');
    await fn({ tenantId, microsoftUserId });
  }

  async detachTenantUsers(organizationId: string, tenantId: string): Promise<void> {
    const fn = httpsCallable<{ organizationId: string; tenantId: string }, { detached: number }>(
      this.firebase.functions, 'detachTenantUsers'
    );
    await fn({ organizationId, tenantId });
  }

  /** Help coaching for the whole organization: on unless an admin turned it off. */
  async getCoachingEnabled(organizationId: string): Promise<boolean> {
    const snap = await getDoc(doc(this.firebase.firestore, 'organizations', organizationId, 'settings', 'coaching'));
    return coachingEnabledFrom(snap.data());
  }

  /** Turn Help coaching (tips and weekly recaps) on or off for every member (settings.manage). */
  async setCoachingEnabled(organizationId: string, enabled: boolean, uid: string): Promise<void> {
    await setDoc(
      doc(this.firebase.firestore, 'organizations', organizationId, 'settings', 'coaching'),
      { enabled, updatedAt: serverTimestamp(), updatedBy: uid },
      { merge: true },
    );
  }

  /** Persist the set of Entra directory attributes the org has opted into for
   *  usage-report segmentation. */
  async updateUsageReportSegments(
    organizationId: string,
    segments: UsageReportSegment[],
  ): Promise<void> {
    const orgRef = doc(this.firebase.firestore, 'organizations', organizationId);
    await updateDoc(orgRef, { usageReportSegments: segments });
  }

  /** Apply the org's Entra role assignments now (backend syncEntraRolesNow —
   *  the same sync that runs hourly): changed roles are written, members who
   *  lost every assignment are removed, anything uncertain is left alone. */
  async syncEntraRolesNow(
    organizationId: string,
    opts: { dryRun?: boolean; confirmRemovals?: boolean } = {},
  ): Promise<EntraRoleSyncResult> {
    const fn = httpsCallable<{ organizationId: string; dryRun?: boolean; confirmRemovals?: boolean }, EntraRoleSyncResult>(
      this.firebase.functions, 'syncEntraRolesNow');
    return (await fn({ organizationId, ...opts })).data;
  }

  /** Trigger a backend re-sync of the given Entra attributes for every existing
   *  member. Used when an admin newly enables a field so historical members get
   *  populated rather than only those who log in afterward. */
  async backfillMicrosoftAttributes(
    organizationId: string,
    fields: EntraSegmentField[],
  ): Promise<{ updated: number; skipped: number }> {
    const fn = httpsCallable<
      { organizationId: string; fields: EntraSegmentField[] },
      { updated: number; skipped: number }
    >(this.firebase.functions, 'backfillMicrosoftAttributes');
    const result = await fn({ organizationId, fields });
    return result.data;
  }

}

/** Backend `syncEntraRolesNow` result (functions/sdk microsoft-sso.ts OrgRoleSyncResult). */
export interface EntraRoleSyncResult {
  updated: number;
  unchanged: number;
  removed: number;
  removalBlocked: number;
  unassigned: number;
  unknown: number;
  notLinked: number;
  failedTenantIds: string[];
  /** dryRun only. */
  wouldUpdate?: number;
  wouldRemove?: number;
  /** Who is (dryRun: would be) removed. */
  people: { uid: string; name: string; email: string }[];
}
